import React, { useState } from 'react';
import { 
  Shield, 
  Lock, 
  Mail, 
  MessageSquare, 
  Eye, 
  EyeOff,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Clock,
  Smartphone
} from 'lucide-react';

interface LoginScreenProps {
  onLoginSuccess: (sessionToken: string, userData: any) => void;
}

interface UserData {
  username: string;
  email: string;
  whatsapp: string;
  role: string;
}

type AuthStep = 'password' | 'otp-method' | 'otp-verification';

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const [currentStep, setCurrentStep] = useState<AuthStep>('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Step 1: Password authentication
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  
  // Step 2: OTP method selection and verification
  const [otpMethod, setOtpMethod] = useState<'email' | 'whatsapp'>('email');
  const [otp, setOtp] = useState('');
  const [_otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Step 1: Validate 10-digit password
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    
    if (!username.trim()) {
      setError('Please enter your username');
      return;
    }
    
    if (password.length !== 10 || !/^\d{10}$/.test(password)) {
      setError('Password must be exactly 10 digits');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/login-step1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check if simple mode (no OTP required)
        if (data.simple_mode && data.session_token) {
          setSuccess('Login successful! Redirecting...');
          
          // Store session token
          localStorage.setItem('sessionToken', data.session_token);
          localStorage.setItem('userData', JSON.stringify(data.user_data));
          
          // Call success callback
          setTimeout(() => {
            onLoginSuccess(data.session_token, data.user_data);
          }, 1000);
        } else {
          // Normal mode - proceed to OTP
          setUserData(data.user_data);
          setSuccess(data.message);
          setCurrentStep('otp-method');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Authentication failed');
      }
    } catch (error) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Send OTP
  const handleSendOTP = async () => {
    if (!userData) return;
    
    clearMessages();
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          delivery_method: otpMethod,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message);
        setOtpSent(true);
        setCurrentStep('otp-verification');
        
        // Start countdown
        setCountdown(300); // 5 minutes
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to send OTP');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Verify OTP
  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData) return;
    
    clearMessages();
    
    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setError('OTP must be exactly 6 digits');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch('/api/auth/login-step2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: userData.username,
          otp: otp,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSuccess('Login successful! Redirecting...');
        
        // Store session token
        localStorage.setItem('sessionToken', data.session_token);
        localStorage.setItem('userData', JSON.stringify(userData));
        
        // Call success callback
        setTimeout(() => {
          onLoginSuccess(data.session_token, userData);
        }, 1000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid OTP');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleBackToPassword = () => {
    setCurrentStep('password');
    setOtpSent(false);
    setOtp('');
    setCountdown(0);
    clearMessages();
  };

  const handleBackToMethod = () => {
    setCurrentStep('otp-method');
    setOtp('');
    clearMessages();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 text-center">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield size={32} />
          </div>
          <h1 className="text-2xl font-bold">🎯 Sales Assistant</h1>
          <p className="text-sm opacity-90 mt-1">Secure Control Center Access</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'password' ? 'bg-blue-600 text-white' : 'bg-green-500 text-white'
              }`}>
                {currentStep === 'password' ? '1' : <CheckCircle size={16} />}
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'otp-method' ? 'bg-blue-600 text-white' : 
                currentStep === 'otp-verification' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {currentStep === 'otp-verification' ? <CheckCircle size={16} /> : '2'}
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep === 'otp-verification' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                3
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center space-x-2">
              <AlertCircle size={16} className="text-red-600 flex-shrink-0" />
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center space-x-2">
              <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
              <span className="text-green-800 text-sm">{success}</span>
            </div>
          )}

          {/* Step 1: Password Authentication */}
          {currentStep === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  10-Digit Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter 10-digit password"
                    maxLength={10}
                    pattern="\d{10}"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Must be exactly 10 digits</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Lock size={16} />
                    <span>Verify Password</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 2: OTP Method Selection */}
          {currentStep === 'otp-method' && userData && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Verification Method</h3>
                <p className="text-sm text-gray-600">
                  Select how you'd like to receive your 6-digit verification code
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="otpMethod"
                    value="email"
                    checked={otpMethod === 'email'}
                    onChange={(e) => setOtpMethod(e.target.value as 'email')}
                    className="mr-3"
                  />
                  <Mail size={20} className="text-blue-600 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">Email</div>
                    <div className="text-sm text-gray-600">{userData.email}</div>
                  </div>
                </label>

                <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="otpMethod"
                    value="whatsapp"
                    checked={otpMethod === 'whatsapp'}
                    onChange={(e) => setOtpMethod(e.target.value as 'whatsapp')}
                    className="mr-3"
                  />
                  <Smartphone size={20} className="text-green-600 mr-3" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">WhatsApp</div>
                    <div className="text-sm text-gray-600">{userData.whatsapp}</div>
                  </div>
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleBackToPassword}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <MessageSquare size={16} />
                      <span>Send Code</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: OTP Verification */}
          {currentStep === 'otp-verification' && userData && (
            <form onSubmit={handleOTPSubmit} className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Enter Verification Code</h3>
                <p className="text-sm text-gray-600">
                  We've sent a 6-digit code to your {otpMethod === 'email' ? 'email' : 'WhatsApp'}
                </p>
                {countdown > 0 && (
                  <div className="flex items-center justify-center space-x-1 mt-2 text-sm text-blue-600">
                    <Clock size={14} />
                    <span>Expires in {formatTime(countdown)}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  6-Digit Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  pattern="\d{6}"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter the 6-digit code you received</p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleBackToMethod}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      <span>Verify & Login</span>
                    </>
                  )}
                </button>
              </div>

              {countdown === 0 && (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium py-2"
                >
                  Resend verification code
                </button>
              )}
            </form>
          )}

          {/* Demo Credentials */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Demo Credentials:</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div><strong>Admin:</strong> username: "admin" | password: "1234567890"</div>
              <div><strong>Staff:</strong> username: "staff" | password: "9876543210"</div>
              <div className="text-green-600 mt-2">
                <strong>Simple Mode:</strong> Only password required (OTP temporarily disabled)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};